import { type MutableRefObject, useCallback } from 'react';

import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { type useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import { useRemixThinkingBarProgress } from './useRemixThinkingBarProgress.ts';

type ExperienceTracker = ReturnType<typeof useAIMateExperienceTracker>;

type UseRemixPreviewFlowOptions = {
	entryPoint: 'editor' | 'renderer';
	experienceTracker: ExperienceTracker;
	dynamicUiType: string | undefined;
	selectedSubTypeRef: MutableRefObject<string | undefined>;
	// Refs (rather than direct values) because `openAIFCStagingModal` is produced by
	// `useRemixToAIFC`, which itself takes the `onActionReady` returned by this hook —
	// the ref breaks that cycle. The other refs are mutated by container event handlers
	// after this hook runs, so we read `.current` lazily at action time.
	openAIFCStagingModalRef: MutableRefObject<() => void>;
	isRemixCancelledRef: MutableRefObject<boolean>;
	// Optional ref whose `.current` value is read at action time and added to the
	// REMIX_RENDERS_IN_MODAL start attributes for backend debugging.
	conversationIdRef?: MutableRefObject<string | undefined>;
	shouldOpenPreview?: () => boolean;
	onAfterOpenPreview?: () => void;
};

type UseRemixPreviewFlowResult = {
	showProgressBar: boolean;
	isComplete: boolean;
	onCompleteAnimationFinished: () => void;
	onActionReady: () => void;
	abortRendersInModal: () => void;
	resetCompletion: () => void;
};

/**
 * Wiring for the Editor/Renderer remix preview flow: experience tracking,
 * opening the AIFC staging modal, the progress-bar deferral, and the
 * `isRemixCancelled` guard around `onActionReady`.
 */
export const useRemixPreviewFlow = ({
	entryPoint,
	experienceTracker,
	dynamicUiType,
	selectedSubTypeRef,
	openAIFCStagingModalRef,
	isRemixCancelledRef,
	conversationIdRef,
	shouldOpenPreview,
	onAfterOpenPreview,
}: UseRemixPreviewFlowOptions): UseRemixPreviewFlowResult => {
	const openPreview = useCallback(() => {
		experienceTracker.succeed({
			name: ExperienceName.REMIX_START,
		});
		experienceTracker.start({
			name: ExperienceName.REMIX_RENDERS_IN_MODAL,
			id: ExperienceName.REMIX_RENDERS_IN_MODAL,
			attributes: {
				product: 'confluence',
				remixType: dynamicUiType,
				remixSubtype: selectedSubTypeRef.current,
				entryPoint,
				conversationChannelId: conversationIdRef?.current,
			},
		});
		openAIFCStagingModalRef.current();
		onAfterOpenPreview?.();
	}, [
		entryPoint,
		experienceTracker,
		dynamicUiType,
		selectedSubTypeRef,
		openAIFCStagingModalRef,
		conversationIdRef,
		onAfterOpenPreview,
	]);

	const openPreviewIfAllowed = useCallback(() => {
		if (shouldOpenPreview?.() === false) {
			return;
		}

		openPreview();
	}, [openPreview, shouldOpenPreview]);

	const abortRendersInModal = useCallback(() => {
		const reason = 'User closed modal before render completed';
		void remixGeneratePreviewExperience.abort({ metadata: { reason } });
		experienceTracker.abort({
			name: ExperienceName.REMIX_RENDERS_IN_MODAL,
			reason,
			attributes: { reason },
		});
	}, [experienceTracker]);

	const {
		showProgressBar,
		isComplete,
		onCompleteAnimationFinished,
		signalActionReady,
		resetCompletion,
	} = useRemixThinkingBarProgress({ onOpenPreview: openPreviewIfAllowed });

	const onActionReady = useCallback(() => {
		if (!isRemixCancelledRef.current) {
			if (dynamicUiType !== 'diagram' && dynamicUiType !== 'whiteboard') {
				remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.ACTION_COMPLETE_RECEIVED);
			}
			signalActionReady();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- ref identity is stable; reading .current does not need to be a dep
	}, [dynamicUiType, signalActionReady]);

	return {
		showProgressBar,
		isComplete,
		onCompleteAnimationFinished,
		onActionReady,
		abortRendersInModal,
		resetCompletion,
	};
};
